import { useCallback, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { CircularProgress } from '@/components/shared/CircularProgress';
import { abyssColors } from '@/theme/colors';
import { calculatePercentage, getTemplateFieldData, titleCaseSentence } from '@/utils';
import { localiseMeasurement } from '@/utils/unitSystem';
import { useDefectLegendColors } from '@/components/Analysis/shared/hooks/useDefectLegendColors';
import { coatingConditionLegend, CONSOLIDATED_DEGREE_OF_RUSTING } from '@/constants';
import {
  EquipmentDetailSubTypeEnum,
  EquipmentDetailTypeEnum,
  OptionFieldTypeEnum,
  SortTypeEnum,
  UnitsEnum,
} from '@/__generated__/graphql';

export const useFieldFormatting = (
  assemblyDetails: Record<string, string> | undefined,
  assemblyArea: number
) => {
  const assemblyTemplate = useAtomValue(state.assemblyTemplate);
  const unitSystem = useAtomValue(state.unitSystem);
  const { defectLegendColors } = useDefectLegendColors();

  const getFieldName = useCallback(
    (key: string) =>
      assemblyTemplate?.fields.find((field) => field.key === key)?.name ??
      titleCaseSentence(key.replace(/_/g, ' ')),
    [assemblyTemplate?.fields]
  );

  const getFieldUnit = useCallback(
    (unit: UnitsEnum, fieldValue: number) => {
      return localiseMeasurement({ unitSystem, sourceUnit: unit, value: Number(fieldValue) });
    },
    [unitSystem]
  );

  const formatValue = useCallback(
    (key: string, value: string | number) => {
      const field = assemblyTemplate?.fields.find((field) => field.key === key);

      if (field?.displayConfig?.sortType === SortTypeEnum.Number) {
        const formattedValue = field.unit
          ? `${getFieldUnit(field.unit, Number(value))}`
          : `${Number(value)}`;
        return !formattedValue || Number.isNaN(parseFloat(formattedValue)) ? 'N/A' : formattedValue;
      }

      return value ?? 'N/A';
    },
    [assemblyTemplate?.fields, getFieldUnit]
  );

  const getConditionColor = useCallback(
    (title: string) =>
      defectLegendColors[title] ??
      coatingConditionLegend[title as keyof typeof coatingConditionLegend] ??
      'black',
    [defectLegendColors]
  );

  const calculateAssemblyValues = useCallback(
    (rawValue: string | number) => {
      const parsed = parseFloat(rawValue.toString());
      if (isNaN(parsed)) {
        return {
          corrosionValue: 'N/A',
          assemblyRatio: '0.00%',
        };
      }
      const fixed = parsed.toFixed(3);
      const percent = calculatePercentage(Number(assemblyArea), parsed);
      return {
        corrosionValue: localiseMeasurement({
          unitSystem,
          sourceUnit: UnitsEnum.SquareMeter,
          value: Number(fixed),
        }),
        assemblyRatio: isNaN(percent) ? '0.00%' : `${percent}%`,
      };
    },
    [assemblyArea, unitSystem]
  );

  const renderDefectRow = useCallback(
    (label: string, ratio: string, color: string) => (
      <Stack direction="row" alignItems="center" sx={{ width: '100%' }} key={label}>
        <Typography
          variant="body1"
          sx={{
            flexGrow: 1,
            fontWeight: 500,
            color: abyssColors.primary[500],
            fontSize: '1.2rem',
          }}
        >
          {label}
        </Typography>
        <CircularProgress
          value={Number(ratio.replace('%', '')) || 0}
          backgroundColor={abyssColors.primary[100]}
          progressColor={color}
          size={25}
        />
      </Stack>
    ),
    []
  );

  const getFormattedValueDetails = useCallback(
    (key: string, value: string | number) => {
      if (!assemblyDetails) return;

      const consolidated = assemblyTemplate?.equipmentDetails?.find(
        (detail) => detail.subType === EquipmentDetailSubTypeEnum.DefectConsolidatedDegreeOfRusting
      );

      if (consolidated && key === CONSOLIDATED_DEGREE_OF_RUSTING) {
        const title = assemblyDetails[CONSOLIDATED_DEGREE_OF_RUSTING] ?? 'N/A';
        const { corrosionValue, assemblyRatio } = calculateAssemblyValues(value);
        const color = getConditionColor(title.toString());
        return renderDefectRow(`${corrosionValue} - ${assemblyRatio}`, assemblyRatio, color);
      }

      const templateList = assemblyTemplate?.equipmentDetails?.filter(
        (detail) => detail.type === EquipmentDetailTypeEnum.TemplateList
      );

      const matchingRows =
        templateList?.flatMap((item) => {
          const fields = item.options.filter(
            (option) =>
              option.type === OptionFieldTypeEnum.CircularProgressBar && option.fieldKey === key
          );

          const fieldsData = getTemplateFieldData(fields, assemblyDetails);

          return fieldsData
            .filter((field) => field.name)
            .map((field) => {
              const title = field.name!;
              const { corrosionValue, assemblyRatio } = calculateAssemblyValues(value);
              const color = getConditionColor(title);
              return renderDefectRow(`${corrosionValue} - ${assemblyRatio}`, assemblyRatio, color);
            });
        }) ?? [];

      if (matchingRows.length > 0) {
        return matchingRows;
      }

      return (
        <Typography
          variant="body1"
          sx={{ fontWeight: 500, fontSize: '1.2rem', color: abyssColors.primary[500] }}
        >
          {formatValue(key, value)}
        </Typography>
      );
    },
    [
      assemblyTemplate?.equipmentDetails,
      formatValue,
      assemblyDetails,
      calculateAssemblyValues,
      getConditionColor,
      renderDefectRow,
    ]
  );

  const displayableFields = useMemo(() => {
    return (
      assemblyTemplate?.fields
        .filter((field) => field.displayInReview)
        .map((field) => field.key)
        .filter((key): key is string => typeof key === 'string') ?? []
    );
  }, [assemblyTemplate]);

  return {
    getFieldName,
    formatValue,
    displayableFields,
    getFormattedValueDetails,
  };
};
